import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import AuthBrandLogo from '../../branding/AuthBrandLogo';
import { identityApi } from '../../api/client';
import { ROUTES } from '../../routes/constants';

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-brand/[0.06] blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-info/[0.05] blur-[100px]" />
        <div className="absolute top-1/2 -left-32 w-[350px] h-[350px] rounded-full bg-bull/[0.03] blur-[80px]" />
      </div>
      <div className="w-full max-w-sm relative z-10">{children}</div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token') || searchParams.get('reset_token') || '';
  const tokenFromHash = (() => {
    if (typeof window === 'undefined') return '';
    const hash = window.location.hash || '';
    const queryPart = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
    if (!queryPart) return '';
    const hashParams = new URLSearchParams(queryPart);
    return hashParams.get('token') || hashParams.get('reset_token') || '';
  })();
  const urlToken = (tokenFromQuery || tokenFromHash || '').trim();

  const [token, setToken]                     = useState(urlToken);
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]                     = useState('');
  const [success, setSuccess]                 = useState(false);
  const [loading, setLoading]                 = useState(false);
  const hasUrlToken = !!urlToken;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUrlToken || !token.trim()) { setError('Invalid reset link. Please use the link sent to your email.'); return; }
    if (!newPassword)                  { setError('New password is required.'); return; }
    if (newPassword.length < 8)        { setError('Password must be at least 8 characters.'); return; }
    if (!confirmPassword)              { setError('Please confirm your new password.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await identityApi.resetPassword(token.trim(), newPassword);
      setSuccess(true);
      setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 2500);
    } catch (err: any) {
      setError(err?.message || 'Reset failed. The token may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell>
        <div className="text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-bull mx-auto" />
          <h2 className="font-semibold text-txt-primary">Password reset!</h2>
          <p className="text-xs text-txt-muted">Redirecting you to sign in…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="text-center mb-8">
        <AuthBrandLogo />
        <p className="text-2xs font-mono text-txt-muted tracking-widest uppercase mt-3">
          Set a new password
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-6 space-y-5 shadow-modal overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />

        {!hasUrlToken && (
          <div className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">
            Invalid or missing reset token. Please open the reset link sent to your email.
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="8+ characters"
            autoComplete="new-password"
            className="input-base"
            disabled={loading}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            autoComplete="new-password"
            className="input-base"
            disabled={loading}
          />
        </div>

        {error && (
          <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{error}</p>
        )}

        <button type="submit" disabled={loading || !hasUrlToken} className="btn-primary w-full justify-center mt-1">
          {loading ? 'Resetting…' : 'Reset Password'}
        </button>

        <div className="border-t border-bg-border/40 pt-3 text-center">
          <Link to={ROUTES.LOGIN} className="text-xs text-txt-muted hover:text-brand transition-colors">
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
